import {Storyblok} from 'storyblok-js-client';
import {REQUEST_TIMEOUT_IN_SECONDS} from '#shared/constants';
import {useApiUrl} from '@/composables/useApiUrl';
import {useRequestTracing} from '@/composables/useRequestTracing';

interface ErrorCause {
	requestURL: string;
	requestMethod: string;
	status?: number;
	statusText?: string;
	originalError?: any;
	body?: any;
}

function isErrorCause(e: unknown): e is ErrorCause {
	return typeof e === 'object' && e !== null
		&& 'requestURL' in e
		&& 'requestMethod' in e
		&& 'originalError' in e;
}

// storyblok-js-client >= 7.7 deletes `version` from every request whose path has no `/cdn/`,
// which is every request through the PHP proxy. It is carried under this key and restored in fetch.
const PROXY_VERSION_PARAM = '_proxy_version';

export function getStoryblokClient(): Storyblok {
	const {requestId, correlationId} = useRequestTracing();

	const client = new Storyblok({
		endpoint: `${useApiUrl()}/api/v1/storyblok`,
		timeout: REQUEST_TIMEOUT_IN_SECONDS,
		fetch: async (url, params) => {
			const normalizedUrl = url instanceof URL
				? url
				: typeof url === 'string'
					? new URL(url)
					: new URL(url.url);
			const proxyVersion = normalizedUrl.searchParams.get(PROXY_VERSION_PARAM);
			if (proxyVersion) {
				normalizedUrl.searchParams.delete(PROXY_VERSION_PARAM);
				normalizedUrl.searchParams.set('version', proxyVersion);
			}
			const method = params?.method?.toUpperCase() ?? 'GET';

			const modifiedParams = {
				...params,
				headers: {
					...params?.headers,
					...(requestId ? {'X-Request-ID': requestId} : {}),
					...(correlationId ? {'X-Correlation-ID': correlationId} : {}),
				},
			};

			try {
				const response = await fetch(normalizedUrl, modifiedParams);

				if (!response.ok) {
					const body = await response.json().catch(() => ({})); // Try to parse JSON, fallback to empty object

					// Použít createError pro správné zachování HTTP status kódu
					const error = createError({
						statusCode: response.status,
						statusMessage: response.statusText,
						message: body?.message || 'API request failed',
						data: {
							requestURL: normalizedUrl.toString(),
							requestMethod: method,
							status: response.status,
							statusText: response.statusText,
							originalError: response,
							body,
						} as ErrorCause,
					});
					throw error;
				}

				return response;
			} catch (e: unknown) {
				// Pokud je to už H3Error/NuxtError (z createError), prostě ho přehodíme
				if (e && typeof e === 'object' && 'statusCode' in e) {
					throw e;
				}

				let body = {};
				if (e instanceof Response) {
					try {
						body = await e.json();
					} catch (parseError) {
						if (import.meta.client) {
							console.error('Failed to parse error response JSON:', parseError);
						}
					}
				} else if (e instanceof Error && isErrorCause(e.cause)) {
					body = e.cause.body;
				}

				/**
				 * Silently handle AbortErrors to prevent storyblok-js-client from rejecting promises
				 * The `_aborted` flag is included as a backdoor if a specific component needs to know.
				 */
				if (e instanceof DOMException && e.name === 'AbortError') {
					const emptyPayload = {
						story: null,
						stories: [],
						_aborted: true,
					};

					return new Response(JSON.stringify(emptyPayload), {
						status: 200,
						statusText: 'OK',
						headers: {'Content-Type': 'application/json'},
					});
				}

				if (e instanceof Error) {
					let statusCode = 500;
					let statusMessage = 'Internal Server Error';

					if (isErrorCause(e.cause)) {
						if (e.cause.status) {
							statusCode = e.cause.status;
						}
						if (e.cause.statusText) {
							statusMessage = e.cause.statusText;
						}
					}

					throw createError({
						statusCode,
						statusMessage,
						message: e.message || 'API request failed',
						data: {
							requestURL: normalizedUrl.toString(),
							requestMethod: method,
							originalError: e,
							body,
						},
					});
				}

				throw e;
			}
		},
	});

	const get = client.get.bind(client);
	client.get = ((slug, params, fetchOptions) => {
		if (!params?.version) {
			return get(slug, params, fetchOptions);
		}

		const {version, ...rest} = params;
		return get(slug, {...rest, [PROXY_VERSION_PARAM]: version} as unknown as typeof params, fetchOptions);
	}) as Storyblok['get'];

	return client;
}
